export const getProductOptions = (orders = []) => {
  const byProduct = new Map();

  orders.forEach((order) => {
    const productId = String(order?.itemId || order?.ItemId || '').trim();
    if (!productId) return;

    const orderNumber = String(order?.id || order?.Id || order?.productionOrderNumber || '').trim();
    const existing = byProduct.get(productId);

    if (!existing) {
      byProduct.set(productId, {
        value: productId,
        label: productId,
        latestOrderNumber: orderNumber,
      });
      return;
    }

    const existingOrderNumber = Number(existing.latestOrderNumber || 0);
    const candidateOrderNumber = Number(orderNumber || 0);

    if (candidateOrderNumber > existingOrderNumber) {
      existing.latestOrderNumber = orderNumber;
      existing.label = `${productId} (${orderNumber})`;
    }
  });

  return Array.from(byProduct.values()).sort((a, b) => a.label.localeCompare(b.label));
};

export const getLatestProductionOrderForProduct = (orders = [], productId = '') => {
  const normalizedProductId = String(productId || '').trim();
  if (!normalizedProductId) return null;

  const matchingOrders = orders.filter((order) => String(order?.itemId || order?.ItemId || '').trim() === normalizedProductId);
  if (matchingOrders.length === 0) return null;

  return matchingOrders
    .slice()
    .sort((a, b) => {
      const aNumber = Number(a?.id || a?.Id || 0);
      const bNumber = Number(b?.id || b?.Id || 0);
      return bNumber - aNumber;
    })[0];
};

export const buildBomRows = ({ rows = [], productOrderLookup = {}, recursive = false }) => {
  const visit = (currentRows, multiplier = 1) => {
    return currentRows.flatMap((row) => {
      const quantity = Number(row.quantity || 0) * multiplier;
      const childOrder = recursive ? productOrderLookup[row.id] : null;

      if (childOrder && childOrder.rows && childOrder.rows.length > 0) {
        return visit(childOrder.rows, quantity);
      }

      return [{
        ...row,
        quantity,
      }];
    });
  };

  return visit(rows);
};

export const consolidateBomRows = (rows = []) => {
  const grouped = new Map();

  rows.forEach((row) => {
    const priceValue = row.price ?? row.PurchasePrice ?? row.purchasePrice ?? '';
    const key = JSON.stringify({
      id: row.id,
      Description: row.Description || row.description || '',
      place: row.place || row.Place || '',
      ECCN: row.ECCN || '',
      HTSUS: row.HTSUS || '',
      COO: row.COO || '',
      price: priceValue,
    });

    if (!grouped.has(key)) {
      grouped.set(key, {
        id: row.id,
        Description: row.Description || row.description || '',
        productionOrderNumber: row.productionOrderNumber ? String(row.productionOrderNumber) : '',
        quantity: Number(row.quantity || 0),
        place: row.place || row.Place || '',
        // stock is per article, so it is carried over rather than summed
        QuantityInStock: row.QuantityInStock,
        ECCN: row.ECCN || '',
        HTSUS: row.HTSUS || '',
        COO: row.COO || '',
        price: priceValue,
        extendedPrice: Number(row.quantity || 0) * Number(priceValue || 0),
      });
      return;
    }

    const existing = grouped.get(key);
    existing.quantity = Number(existing.quantity || 0) + Number(row.quantity || 0);
    existing.extendedPrice = Number(existing.quantity || 0) * Number(existing.price || 0);
    if (existing.QuantityInStock == null) existing.QuantityInStock = row.QuantityInStock;

    const orderNumbers = new Set(
      [existing.productionOrderNumber, row.productionOrderNumber]
        .filter(Boolean)
        .flatMap((value) => String(value).split(',').map((part) => part.trim()).filter(Boolean))
    );

    existing.productionOrderNumber = Array.from(orderNumbers).sort((a, b) => String(a).localeCompare(String(b))).join(', ');
  });

  return Array.from(grouped.values()).sort((a, b) => String(a.id).localeCompare(String(b.id)));
};
